import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { inferOutput } from '@trpc/tanstack-react-query';
import { useTRPC } from '../trpc.js';
import { formatDay } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';
import { CollapsibleCard } from './CollapsibleCard.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface PeopleChangesProps {
  /** The funds selected on the page. None means the whole main list. */
  selectedFundIds: readonly string[];
}

/** How many changes the card shows before "Load more". */
const PAGE_SIZE = 25;

type ProposalsPage = inferOutput<ReturnType<typeof useTRPC>['proposals']['list']>;

/** Each source as a person reads it. */
const ORIGIN: Record<string, string> = {
  team_page: 'team page',
  linkedin: 'LinkedIn',
  reported: 'reported',
  research: 'research',
  dealroom: 'Dealroom',
};

/**
 * Where people work, kept current: the changes the team page check, the
 * LinkedIn search and reported moves have found, each waiting on a person.
 * Nothing about a role changes until somebody presses Accept here (or an
 * agent decides it over MCP), and accepting writes it the way a typed edit
 * would, with the proposal named on the change.
 */
export function PeopleChanges(props: PeopleChangesProps): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const listInput = { about: 'people', limit: PAGE_SIZE } as const;
  const proposals = useInfiniteQuery(
    trpc.proposals.list.infiniteQueryOptions(listInput, {
      getNextPageParam: (page) => page.nextCursor,
    }),
  );
  const listKey = trpc.proposals.list.infiniteQueryKey(listInput);
  const runs = useQuery(trpc.people.runs.queryOptions());

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: trpc.investors.list.queryKey() });
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.people.runs.queryKey() });
  };
  const onError = (error: { message: string }): void => toast(error.message);

  // The decided change leaves the loaded pages at once and the total drops by
  // one; the pages are not refetched, so the list keeps its place. The cursor
  // is a position in the order, not an offset, so the next page is unaffected.
  const decide = useMutation(
    trpc.proposals.decide.mutationOptions({
      onMutate: async (input) => {
        await queryClient.cancelQueries({ queryKey: listKey });
        const before =
          queryClient.getQueryData<InfiniteData<ProposalsPage, string | null>>(listKey);
        queryClient.setQueryData<InfiniteData<ProposalsPage, string | null>>(listKey, (data) =>
          data === undefined
            ? data
            : {
                ...data,
                pages: data.pages.map((page) => ({
                  ...page,
                  proposals: page.proposals.filter((row) => row.id !== input.proposalId),
                  total: Math.max(0, page.total - 1),
                })),
              },
        );
        return { before };
      },
      onSuccess: (_, input) => {
        refresh();
        toast(
          input.decision === 'reject'
            ? 'Rejected.'
            : input.samePersonId === undefined
              ? 'Accepted.'
              : 'Accepted as the same person.',
        );
      },
      onError: (error, _input, context) => {
        if (context?.before !== undefined) queryClient.setQueryData(listKey, context.before);
        void queryClient.invalidateQueries({ queryKey: listKey });
        onError(error);
      },
    }),
  );

  const checkTeamPages = useMutation(
    trpc.people.checkTeamPages.mutationOptions({
      onSuccess: () => {
        refresh();
        toast('Reading team pages. Changes appear here as they are found.');
      },
      onError,
    }),
  );

  const shown = (proposals.data?.pages ?? []).flatMap((page) => page.proposals);
  const waiting = proposals.data?.pages.at(-1)?.total ?? 0;
  const scope =
    props.selectedFundIds.length === 0 ? 'main list' : `${props.selectedFundIds.length} selected`;

  return (
    <CollapsibleCard id="people-changes" title="People who may have moved" count={waiting}>
      <button
        className="button small"
        type="button"
        disabled={checkTeamPages.isPending}
        onClick={() =>
          checkTeamPages.mutate(
            props.selectedFundIds.length === 0 ? {} : { fundIds: [...props.selectedFundIds] },
          )
        }
      >
        Check team pages ({scope})
      </button>
      {(runs.data ?? []).map((run) => (
        <p className="subtle" key={run.jobId}>
          {run.kind === 'team_page_check' ? 'Team pages' : 'LinkedIn'}: {run.status}
          {run.finishedAt === null ? '' : ` ${formatDay(run.finishedAt) ?? ''}`}
          {run.error === null ? '' : ` — ${run.error}`}
        </p>
      ))}
      <p className="subtle">{waitingPhrase(waiting)}</p>

      {shown.map((proposal) => (
        <div className="review-row" key={proposal.id}>
          <p className="review-said">
            <strong>{proposal.description ?? proposal.columnName}</strong>
          </p>
          {proposal.evidence !== null && <p className="subtle">{proposal.evidence}</p>}
          <p className="subtle">
            {ORIGIN[proposal.origin?.kind ?? ''] ?? 'unknown source'} ·{' '}
            {formatDay(proposal.createdAt)}
            {proposal.sourceUrl !== null && (
              <>
                {' · '}
                <a href={proposal.sourceUrl} target="_blank" rel="noreferrer">
                  source
                </a>
              </>
            )}
          </p>
          {proposal.possibleSamePerson.map((person) => (
            <div className="review-actions" key={person.personId}>
              <span className="subtle">
                Could be {person.name}: {rolesPhrase(person.roles)}
              </span>
              <button
                className="button small"
                type="button"
                disabled={decide.isPending}
                onClick={() =>
                  decide.mutate({
                    proposalId: proposal.id,
                    decision: 'accept',
                    samePersonId: person.personId,
                  })
                }
              >
                Same person
              </button>
            </div>
          ))}
          <div className="review-actions">
            <button
              className="button small"
              type="button"
              disabled={decide.isPending}
              title={proposal.possibleSamePerson.length > 0 ? 'Accept as a new person' : undefined}
              onClick={() => decide.mutate({ proposalId: proposal.id, decision: 'accept' })}
            >
              Accept
            </button>
            <button
              className="button small"
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ proposalId: proposal.id, decision: 'reject' })}
            >
              Reject
            </button>
          </div>
        </div>
      ))}

      {proposals.hasNextPage && (
        <div className="review-more">
          <p className="subtle">
            Showing {shown.length} of {waiting}
          </p>
          <button
            className="button small"
            type="button"
            disabled={proposals.isFetchingNextPage}
            onClick={() => void proposals.fetchNextPage()}
          >
            {proposals.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </CollapsibleCard>
  );
}

type PossibleRole =
  ProposalsPage['proposals'][number]['possibleSamePerson'][number]['roles'][number];

function rolesPhrase(roles: readonly PossibleRole[]): string {
  return roles
    .map((role) => {
      const place = role.title === null ? role.fundName : `${role.title} at ${role.fundName}`;
      return role.current ? place : `formerly ${place}`;
    })
    .join('; ');
}

function waitingPhrase(count: number): string {
  if (count === 0) return 'no changes waiting for review';
  return count === 1 ? '1 change waiting for review' : `${count} changes waiting for review`;
}
